import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ExpandLessTwoTone, LogoutOutlined } from "@mui/icons-material";
import ExpandMoreTwoToneIcon from "@mui/icons-material/ExpandMoreTwoTone";
import { Box, Button, Divider, Hidden, lighten, List, ListItem, ListItemText, Popover, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { logOut, selectAuth } from "@slices/authSlice";
import { selectRoleBasedProperty, setRoleBasedProperty } from "@slices/roleBasedPropertySlice";
import { UserRoles } from "@enums/UserRoles";
import { getRoleBasedProperty, MenuIcons } from "@components/RoleBasedProperty";
import MenuIcon from "@layouts/MenuIcon";
import TFFDMAvatar from "@components/TFFDMAvatar";
import { pageRoutes } from "@constants/pageRoutes";
import { sendEvent } from "@utilities/googleAnalytics";
import { GoogleAnalyticsEvents } from "@constants/googleAnalyticsEvents";

const UserBoxButton = styled(Button)(
    ({ theme }: any) => `
    padding-left: ${theme.spacing(1)};
    padding-right: ${theme.spacing(1)};
`
);

const UserBoxText = styled(Box)(
    ({ theme }: any) => `
    text-align: left;
    padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
    ({ theme }: any) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
    ({ theme }: any) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

function HeaderUserBox() {
    const auth = useAppSelector(selectAuth);
    const userMenuItems = useAppSelector(selectRoleBasedProperty).accountMenu || [];
    const dispatch = useAppDispatch();
    let navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const handleLogout = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        dispatch(logOut());
        dispatch(setRoleBasedProperty(getRoleBasedProperty()));
        navigate(pageRoutes.LOGIN);
    };

    return (
        <Box>
            <UserBoxButton
                color="secondary"
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    sendEvent(GoogleAnalyticsEvents.UserMenu);
                    handleOpen(event);
                }}
            >
                <Box display={"flex"}>
                    <TFFDMAvatar imageUrl={auth.userInfo?.profilePicUrl} />
                    <Box>
                        <UserBoxText>
                            {auth.userInfo?.username && <UserBoxLabel variant="body1">{auth.userInfo?.username}</UserBoxLabel>}
                            <UserBoxDescription variant="body2">
                                {auth.userInfo?.role === UserRoles.USER ? "Donor" : auth.userInfo?.role}
                            </UserBoxDescription>
                        </UserBoxText>
                    </Box>
                </Box>
                <Hidden smDown>
                    <Box ml={1}>{!open ? <ExpandMoreTwoToneIcon /> : <ExpandLessTwoTone />}</Box>
                </Hidden>
            </UserBoxButton>
            <Popover
                anchorEl={anchorEl}
                onClose={handleClose}
                open={open}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        style: { width: "250px" },
                    },
                }}
            >
                <List component="nav">
                    {userMenuItems.map(({ title, navigateTo, iconName }) => {
                        return (
                            <ListItem
                                key={title}
                                to={navigateTo}
                                component={NavLink}
                                onClick={() => {
                                    sendEvent("button_".concat(title.toLowerCase().replaceAll(" ", "_")).concat("_user_menu"));
                                    handleClose();
                                }}
                            >
                                {MenuIcons[iconName].iconElement}
                                <ListItemText primary={title} sx={{ ml: 1 }} />
                            </ListItem>
                        );
                    })}
                </List>
                <Divider />
                <Box sx={{ m: 1 }}>
                    <Button
                        color="error"
                        sx={{ display: "flex", justifyContent: "flex-start" }}
                        fullWidth
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            sendEvent(GoogleAnalyticsEvents.LogoutButton);
                            handleLogout(event);
                        }}
                    >
                        <MenuIcon icon={<LogoutOutlined sx={{ ml: 0.5, mr: 1 }} />} />
                        Log out
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
}

export default HeaderUserBox;
